import { every, filter, get, includes, isEmpty, parseInt } from 'lodash/fp';
import getParentProductId from '@ahmdigital/logic/lib/utils/get-parent-product-id';

import { ProductType } from '../../../../../types/product';
import constants from '../../../../../ahm-constants';
import getUnavailableProducts from '../get-unavailable-products';

const { COMBINED_PRODUCT_COMPONENTS, PRODUCT_TYPE } = constants;

const RADIX = 10;
const DEFAULT_EXCESS = 750;

type Props = {
  customer: {
    hospitalExcess: string;
  };
  parentProductIds: string[];
  productIdsWithExcess: Record<string, number[]> | undefined;
  products: ProductType[];
};
/**
 * Filters the products returned from redux for the variable excess feature.
 *
 * @param {object} param - An object.
 * @param {number} param.customer - The redux customer state, contains the selected excess value.
 * @param {Array.string} param.parentProductIds - Each of the comparer products parent ids.
 * @param {object} param.productIdsWithExcess - Contains keys of each parent id. Each value is the applicable excess.
 * @param {object[]} param.products - Products retrieved from redux for the comparer needs type.
 */
const getVariableExcessComparerProducts = ({
  customer,
  parentProductIds,
  productIdsWithExcess,
  products,
}: Props): ProductType[] => {
  const extrasOnlyProducts = every({ type: PRODUCT_TYPE.EXTRAS }, products);
  if (isEmpty(productIdsWithExcess) && !extrasOnlyProducts) {
    return [];
  }
  const customerExcess = parseInt(RADIX, get('hospitalExcess', customer));
  const unavailableProductIds = getUnavailableProducts({ customerExcess, parentProductIds, productIdsWithExcess });

  return filter((product) => {
    const productExcess = get('charges.excess.amount', product);
    const parentId = get('parentProductId', product);
    const productId = get('id', product);

    const softBundleId = getParentProductId(get(`${productId}.hospital`, COMBINED_PRODUCT_COMPONENTS));
    const idToCheckIsAvailable = softBundleId || parentId;
    const excessMatchesCustomer = productExcess === customerExcess;
    const isExtrasProduct = get('type', product) === PRODUCT_TYPE.EXTRAS;
    const productIsUnavailable = includes(idToCheckIsAvailable, unavailableProductIds);
    const showIsUnavailable = productIsUnavailable && productExcess === DEFAULT_EXCESS;
    return excessMatchesCustomer || isExtrasProduct || showIsUnavailable;
  }, products) as ProductType[];
};

export default getVariableExcessComparerProducts;
